import { useMemo, useState } from 'react';
import { Card } from '../../components';
import { ScrubChart, type ScrubPoint } from '../../components/charts/ScrubChart';
import { ScrubHeader } from '../../components/charts/ScrubHeader';
import type { AppData } from '../../data/types';
import { formatCurrency } from '../../lib/currency';
import { addDays, fromIsoDate, toIsoDate } from '../../lib/date';

type Props = {
  data: AppData;
  today: Date;
};

const DAYS = 30;

function formatShortDate(iso: string): string {
  return fromIsoDate(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Cumulative PYF savings over the last 30 days. Combines auto-PYF
 * reserve (hourly × workdays elapsed) with manual savings transactions
 * and presents a finger-scrub area chart.
 */
export function SavingsProgress({ data, today }: Props) {
  const [scrub, setScrub] = useState<ScrubPoint | null>(null);

  const { rows, latest, weekDelta } = useMemo(() => {
    const hourlyRate = data.profile.hourlyRate ?? 0;
    const wdpw = Math.min(7, Math.max(1, data.profile.workDaysPerWeek ?? 5));
    const isPyfActive = data.profile.budgetModel === 'pyf' && hourlyRate > 0;

    const savingsCatIds = new Set(
      data.categories
        .filter((c) => c.bucket503020 === 'savings' || c.icon === 'PiggyBank')
        .map((c) => c.id),
    );
    const manualByDay = new Map<string, number>();
    for (const t of data.transactions) {
      if (t.type !== 'expense') continue;
      if (!savingsCatIds.has(t.categoryId)) continue;
      manualByDay.set(t.date, (manualByDay.get(t.date) ?? 0) + t.amount);
    }

    const start = addDays(today, -(DAYS - 1));
    let cum = 0;
    let cum7DaysAgo = 0;
    const rows: ScrubPoint[] = [];
    for (let i = 0; i < DAYS; i++) {
      const d = addDays(start, i);
      const iso = toIsoDate(d);
      const isWorkday = (() => {
        if (wdpw >= 7) return true;
        const dow = d.getDay();
        return dow >= 1 && dow <= wdpw;
      })();
      const auto = isPyfActive && isWorkday ? hourlyRate : 0;
      const manual = manualByDay.get(iso) ?? 0;
      cum += auto + manual;
      if (i === DAYS - 8) cum7DaysAgo = cum;
      rows.push({ time: iso, value: Math.round(cum * 100) / 100 });
    }
    return { rows, latest: cum, weekDelta: cum - cum7DaysAgo };
  }, [data, today]);

  const currency = data.profile.currency;
  const fmt = (n: number) => formatCurrency(n, { currency });
  const latestPoint = rows[rows.length - 1];

  return (
    <Card elevation="sm" className="p-5 rounded-[24px]">
      <ScrubHeader
        title="Savings progress"
        subtitle="Last 30 days"
        currentValue={latest}
        currentLabel={
          latestPoint ? formatShortDate(latestPoint.time) : 'Today'
        }
        scrubValue={scrub?.value ?? null}
        scrubLabel={scrub ? formatShortDate(scrub.time) : null}
        format={fmt}
        color="#0EA66B"
        delta={{ value: weekDelta, period: 'this week' }}
      />
      <div className="mt-3">
        <ScrubChart
          data={rows}
          height={200}
          color="#0EA66B"
          areaFill
          onScrub={setScrub}
        />
      </div>
    </Card>
  );
}
